<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: string | number | null | undefined
  items: ReadonlyArray<Record<string, unknown>> | ReadonlyArray<string>
  itemTitle?: string
  itemValue?: string
  placeholder?: string
  clearable?: boolean
  disabled?: boolean
  icon?: string
  // Pick the only option for the user when there is nothing to choose between.
  // Opt-in: it writes a value the user never picked, which is wrong for filters
  // and for choices that must be made deliberately.
  autoSelectSingle?: boolean
}>(), {
  itemTitle: 'title',
  itemValue: 'value',
  placeholder: undefined,
  icon: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
}>()

defineSlots<{
  option?(props: { item: Record<string, unknown>; selected: boolean }): unknown
  selection?(props: { item: Record<string, unknown> }): unknown
}>()

const dropdownOpen = ref(false)

type NormalizedItem = Record<string, unknown> & { _raw: unknown }

const normalizedItems = computed((): NormalizedItem[] => {
  return props.items.map((item) => {
    if (typeof item === 'string') {
      return { [props.itemTitle]: item, [props.itemValue]: item, _raw: item } as NormalizedItem
    }
    return { ...item, _raw: item } as NormalizedItem
  })
})

watch(
  [() => props.autoSelectSingle, () => props.disabled, normalizedItems, () => props.modelValue],
  ([auto, disabled, items, modelValue]) => {
    if (!auto || disabled || modelValue != null || items.length !== 1) return
    const only = items[0][props.itemValue]
    if (typeof only === 'string' || typeof only === 'number') {
      emit('update:modelValue', only)
    }
  },
  { immediate: true }
)

const selectedItem = computed(() => {
  if (props.modelValue == null) return null
  return normalizedItems.value.find((item) => item[props.itemValue] === props.modelValue) || null
})

const selectedLabel = computed(() => {
  if (!selectedItem.value) return null
  return String(selectedItem.value[props.itemTitle] ?? '')
})

const toggle = () => {
  if (props.disabled) return
  dropdownOpen.value = !dropdownOpen.value
}

const select = (value: string | number) => {
  emit('update:modelValue', value)
  dropdownOpen.value = false
}

const clear = () => {
  emit('update:modelValue', null)
  dropdownOpen.value = false
}

const close = () => {
  dropdownOpen.value = false
}
</script>

<template>
  <div class="base-select" :class="{ disabled }" v-click-outside="close">
    <div class="select-trigger" @click="toggle">
      <v-icon v-if="icon" :icon="icon" size="18" class="select-trigger-icon" />
      <template v-if="selectedItem">
        <slot name="selection" :item="(selectedItem._raw as Record<string, unknown>)">
          <span class="select-text">{{ selectedLabel }}</span>
        </slot>
      </template>
      <span v-else class="select-placeholder">{{ placeholder }}</span>
      <button
        v-if="clearable && modelValue != null"
        type="button"
        class="select-clear"
        @click.stop="clear"
      >
        <v-icon icon="mdi-close" size="14" />
      </button>
      <span class="select-arrow" :class="{ open: dropdownOpen }">&#9662;</span>
    </div>
    <div v-if="dropdownOpen" class="select-dropdown">
      <div
        v-for="item in normalizedItems"
        :key="String(item[itemValue])"
        class="select-option"
        :class="{ selected: modelValue === item[itemValue] }"
        @click="select(item[itemValue] as string | number)"
      >
        <slot name="option" :item="(item._raw as Record<string, unknown>)" :selected="modelValue === item[itemValue]">
          <span>{{ item[itemTitle] }}</span>
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-select {
  position: relative;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.select-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 40px;

  &:hover {
    border-color: rgba(120, 163, 255, 0.25);
    background: rgba(15, 23, 48, 0.6);
  }
}

.select-trigger-icon {
  color: rgba(200, 218, 255, 0.5);
  flex-shrink: 0;
}

.select-text {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-placeholder {
  flex: 1;
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.4);
}

.select-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: rgba(120, 163, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.2);
    color: rgba(200, 218, 255, 0.9);
  }
}

.select-arrow {
  color: rgba(200, 218, 255, 0.4);
  font-size: 0.7rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &.open {
    transform: rotate(180deg);
  }
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  animation: dropdownFadeIn 0.15s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.select-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.1s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.95);
  }

  &.selected {
    background: rgba(0, 176, 255, 0.12);
    color: #64b5f6;
  }

  &:first-child {
    border-radius: 10px 10px 0 0;
  }

  &:last-child {
    border-radius: 0 0 10px 10px;
  }

  &:only-child {
    border-radius: 10px;
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .select-trigger {
    background: rgba(255, 255, 255, 0.6);
    border-color: rgba(28, 110, 227, 0.15);

    &:hover {
      border-color: rgba(28, 110, 227, 0.25);
      background: rgba(255, 255, 255, 0.8);
    }
  }

  .select-trigger-icon {
    color: rgba(15, 23, 42, 0.5);
  }

  .select-text {
    color: rgba(15, 23, 42, 0.9);
  }

  .select-placeholder {
    color: rgba(15, 23, 42, 0.4);
  }

  .select-dropdown {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(28, 110, 227, 0.12);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .select-option {
    color: rgba(15, 23, 42, 0.7);

    &:hover {
      background: rgba(28, 110, 227, 0.05);
      color: rgba(15, 23, 42, 0.95);
    }

    &.selected {
      background: rgba(28, 110, 227, 0.08);
      color: #1976d2;
    }
  }
}
</style>
